import { useState, useEffect } from 'react'
import {
  Typography,
  Stack,
  Container,
  Card,
  Box,
  Switch,
  Button,
  Input,
  Textarea,
  Select,
  Option,
  Divider,
  Alert,
  FormControl,
  FormLabel,
  FormHelperText,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
} from '@mui/joy'
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WhatsApp as WhatsAppIcon,
  Timer as TimerIcon,
  Message as MessageIcon,
  Shield as ShieldIcon,
  SupervisedUserCircle as UserIcon,
  Payment as PaymentIcon,
  Campaign as CampaignIcon,
  Palette as PaletteIcon,
  RestartAlt as RestartAltIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Cloud as CloudIcon,
  MusicNote as MusicNoteIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import api from '../services/api'
import authService from '../services/authService'
import { useAuth } from '../hooks/useAuth'
import { usePermissions } from '../hooks/usePermissions'
import { useThemeColors } from '../context/ThemeContext'

type MetaConversionPolicyKey =
  | 'purchase'
  | 'complete_registration'
  | 'start_trial'
  | 'login'
  | 'website_lead'
  | 'campaign_message_lead'
  | 'kanban_legacy_lead'
  | 'kanban_custom_conversion'

type MetaConversionPolicy = {
  eventKey: MetaConversionPolicyKey
  enabled: boolean
  conversionName: string
  source?: 'company_override' | 'default' | 'default_fallback'
  reason?: string
  settingId?: number
}

type MetaPolicyCompanyOption = {
  id: number
  name: string
}

const META_CONVERSION_POLICY_COMPANY_IDS = [1, 8]
const BUSINESS_META_CONVERSION_POLICY_KEYS: MetaConversionPolicyKey[] = [
  'purchase',
  'campaign_message_lead',
  'kanban_custom_conversion'
]

const META_CONVERSION_POLICY_COPY: Record<MetaConversionPolicyKey, {
  title: string
  description: string
  defaultEnabled: boolean
  defaultName: string
  risk?: string
  codeLabel?: string
}> = {
  purchase: {
    title: 'Purchase / Venta',
    defaultEnabled: true,
    defaultName: 'Venta',
    description: 'Envía la conversión cuando se registra una compra o pago confirmado.'
  },
  complete_registration: {
    title: 'Registro de cliente',
    defaultEnabled: true,
    defaultName: 'Registro de cliente',
    description: 'Envía la conversión cuando una empresa o cliente queda registrado en Chateam.'
  },
  start_trial: {
    title: 'Inicio de prueba',
    defaultEnabled: true,
    defaultName: 'Inicio de prueba',
    description: 'Envía la conversión cuando inicia la prueba o se completa el flujo inicial configurado.'
  },
  login: {
    title: 'Login',
    defaultEnabled: true,
    defaultName: 'Login',
    description: 'Envía la conversión cada vez que un usuario inicia sesión.'
  },
  website_lead: {
    title: 'Lead website/system',
    defaultEnabled: false,
    defaultName: 'Lead',
    description: 'Permite enviar Leads genéricos generados por flujos internos o website.',
    risk: 'Actívalo solo si esa empresa realmente usa ese Lead, para evitar ruido en Meta.'
  },
  campaign_message_lead: {
    title: 'Lead por mensaje de campaña',
    defaultEnabled: false,
    defaultName: 'Lead de campaña',
    description: 'Envía Lead cuando entra un mensaje desde anuncio/campaña Click-to-WhatsApp.',
    risk: 'Si ya optimizas por otra conversión del mismo flujo, revisa que no duplique medición.'
  },
  kanban_legacy_lead: {
    title: 'Lead Funnel legacy',
    defaultEnabled: false,
    defaultName: 'Lead Funnel legacy',
    description: 'Envía el Lead antiguo cuando un ticket cae en una etapa del Funnel de ventas.',
    risk: 'Normalmente debe quedar apagado si usas conversiones de Funnel personalizadas.',
    codeLabel: 'funnel_legacy_lead'
  },
  kanban_custom_conversion: {
    title: 'Conversiones de Funnel personalizadas',
    defaultEnabled: true,
    defaultName: 'Conversión de Funnel personalizada',
    description: 'Permite enviar la conversión configurada en cada etapa del Funnel de ventas con su propio evento/regla.',
    codeLabel: 'funnel_custom_conversion'
  }
}

const VISIBLE_META_CONVERSION_POLICY_KEYS = (
  Object.keys(META_CONVERSION_POLICY_COPY) as MetaConversionPolicyKey[]
).filter(eventKey => eventKey !== 'website_lead')

const getVisibleMetaConversionPolicyKeys = (companyId: number): MetaConversionPolicyKey[] => {
  if (META_CONVERSION_POLICY_COMPANY_IDS.includes(companyId)) {
    return VISIBLE_META_CONVERSION_POLICY_KEYS
  }

  return BUSINESS_META_CONVERSION_POLICY_KEYS
}

export default function Settings() {
  const { user, loading: authLoading } = useAuth()
  const { isSuperAdmin } = usePermissions()
  const { setColors } = useThemeColors()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Avisos de citas creadas en Google Calendar por agentes externos
  const [gaConfig, setGaConfig] = useState<any>(null)
  const [gruposDisponibles, setGruposDisponibles] = useState<any[]>([])
  const [gaActivando, setGaActivando] = useState(false)
  const [gaSincronizando, setGaSincronizando] = useState(false)
  const [showFacebookSecret, setShowFacebookSecret] = useState(false)
  const [newAlertPhone, setNewAlertPhone] = useState('')
  const [newExpirationAlertPhone, setNewExpirationAlertPhone] = useState('')
  const [metaConversionPolicies, setMetaConversionPolicies] = useState<MetaConversionPolicy[]>([])
  const [loadingMetaPolicies, setLoadingMetaPolicies] = useState(false)
  const [savingMetaPolicy, setSavingMetaPolicy] = useState<string | null>(null)
  const [metaPolicyCompanies, setMetaPolicyCompanies] = useState<MetaPolicyCompanyOption[]>([])
  const [selectedMetaPolicyCompanyId, setSelectedMetaPolicyCompanyId] = useState<number | ''>('')
  const [whatsappConnections, setWhatsappConnections] = useState<Array<{
    id: number;
    name: string;
    status: string;
    number: string;
    channel: string;
    Company?: { id: number; name: string };
  }>>([])
  const [activeTab, setActiveTab] = useState(0)
  const selectedMetaPolicyCompanyNumber = Number(selectedMetaPolicyCompanyId || user?.companyId || 0)
  const visibleMetaConversionPolicyKeys = getVisibleMetaConversionPolicyKeys(selectedMetaPolicyCompanyNumber)

  const [settings, setSettings] = useState({
    // Configuraciones de Tickets
    hoursCloseTicketsAuto: '24',
    DirectTicketsToWallets: false,
    closeTicketOnTransfer: false,
    requireQueueOnAccept: 'disabled', // 'enabled' | 'disabled' — exige cola al aceptar ticket pendiente

    // Configuraciones de Chatbot
    chatBotType: 'text',

    // Configuraciones de WhatsApp
    // acceptCallWhatsapp: 'enabled' = se aceptan llamadas | 'disabled' = se rechazan
    // y se envía el mensaje configurado en cada conexión (Whatsapps.callRejectMessage).
    acceptCallWhatsapp: 'enabled',
    acceptAudioMessageContact: 'enabled',
    CheckMsgIsGroup: 'enabled',

    // Configuraciones de Mensajes
    sendGreetingMessageOneQueues: 'enabled',
    sendSignMessage: 'enabled',
    sendFarewellWaitingTicket: 'enabled',
    sendGreetingAccepted: 'enabled',
    sendMsgTransfTicket: 'enabled',
    sendQueuePosition: 'enabled',

    // Mensajes personalizados
    greetingAcceptedMessage: '¡Hola! Tu ticket ha sido aceptado y te atenderemos pronto.',
    transferMessage: 'Tu ticket ha sido transferido a otro departamento.',
    sendQueuePositionMessage: 'Estás en la posición {{position}} de la cola.',

    // Configuraciones de Usuario
    userRandom: 'enabled',
    userRating: 'enabled',

    // Configuraciones de Horarios
    scheduleType: '1',

    // LGPD / Privacidad
    enableLGPD: 'disabled',
    lgpdConsent: 'disabled',
    lgpdDeleteMessage: 'enabled',
    lgpdHideNumber: 'disabled',
    lgpdMessage: 'Sus datos serán tratados de acuerdo con nuestra política de privacidad.',
    lgpdLink: 'https://example.com/privacy',

    // Tags
    requiredTag: 'disabled',

    // Notificaciones
    showNotificationPending: false,

    // Payment Configuration (SuperAdmin only)
    paypalClientId: '',
    paypalSecretKey: '',
    stripePublicKey: '',
    stripeSecretKey: '',

    // Facebook/Instagram App Credentials (para OAuth login)
    facebookAppId: '',
    facebookAppSecret: '',
    metaEmbeddedSignupConfigId: '',
    instagramAppId: '',
    instagramAppSecret: '',

    // Facebook Ads Account Configuration
    facebookAdAccountId: '',
    facebookBusinessId: '',
    facebookSystemUserToken: '',

    // Google Calendar OAuth Credentials
    googleClientId: '',
    googleClientSecret: '',
    // Grupo por defecto para avisos de citas creadas en Google por agentes externos
    alertDefaultGroupTicketId: '',

    // TikTok Credentials
    tiktokClientKey: '',
    tiktokClientSecret: '',
    tiktokBusinessAppId: '',
    tiktokBusinessSecret: '',

    // Theme Colors
    themePrimaryLight: '#5BC2D2',
    themePrimaryDark: '#6FD4E4',
    themeSecondaryLight: '#4caf50',
    themeSecondaryDark: '#4caf50',

    // Alertas WhatsApp nuevas empresas
    newCompanyAlertEnabled: 'disabled',
    newCompanyAlertPhone: '',
    newCompanyAlertWhatsappId: '',
    expirationAlertEnabled: 'disabled',
    expirationAlertPhone: '',
    expirationAlertWhatsappId: '',

    // WhatsApp Cloud API / Coexistencia Meta (solo superadmin)
    cloudAPIEnabled: false,
  })

  useEffect(() => {
    // Solo cargar settings si el auth ya terminó de cargar y tenemos un usuario
    if (!authLoading && user?.companyId) {
      fetchSettings()
      cargarConfigGoogleCitas()
    }
  }, [authLoading, user])

  const fetchSettings = async () => {
    try {
      setLoading(true)

      // Debug: verificar qué tiene el usuario
      // console.log('Settings: User object:', user)
      void 0;
      // console.log('Settings: CompanyId:', user?.companyId)
      void 0;

      if (!user?.companyId) {
        console.error('Settings: No companyId found in user object')

        // Intentar refrescar los datos del usuario
        try {
          // console.log('Settings: Attempting to refresh user data...')
          void 0;
          const userData = await authService.getCurrentUser()
          // console.log('Settings: Refreshed user data:', userData)
          void 0;

          if (userData?.companyId) {
            // Aquí necesitaríamos actualizar el estado del usuario
            // Por ahora, mostrar mensaje de recarga
            toast.error('Sesión expirada. Recarga la página para continuar.')
            return
          } else {
            toast.error('Usuario no tiene compañía asignada. Contacta al administrador.')
            return
          }
        } catch (refreshError) {
          console.error('Settings: Error refreshing user data:', refreshError)
          toast.error('Error de autenticación. Inicia sesión nuevamente.')
          return
        }
      }

      const response = await api.get(`/companySettings/${user.companyId}`)

      if (response.data) {
        // Mapear los datos del backend al estado local
        setSettings(prev => ({
          ...prev,
          ...response.data
        }))
      }

      // Settings de la tabla key/value (no viven en CompaniesSettings).
      // Si el setting no existe en BD, el endpoint puede devolver 404 — lo
      // tratamos como 'disabled' (comportamiento legacy / default OFF).
      try {
        const reqQueueRes = await api.get('/settings/requireQueueOnAccept')
        if (reqQueueRes?.data?.value) {
          setSettings(prev => ({ ...prev, requireQueueOnAccept: reqQueueRes.data.value }))
        }
      } catch (e: any) {
        if (e?.response?.status !== 404) {
          console.warn('No se pudo cargar requireQueueOnAccept:', e?.message)
        }
      }

      // Solo para superadmin: cargar también los payment keys desde company
      if (isSuperAdmin) {
        try {
          const companyResponse = await api.get(`/companies/${user.companyId}`)
          if (companyResponse.data) {
            setSettings(prev => ({
              ...prev,
              paypalClientId: companyResponse.data.paypalClientId || '',
              paypalSecretKey: companyResponse.data.paypalSecretKey || '',
              stripePublicKey: companyResponse.data.stripePublicKey || '',
              stripeSecretKey: companyResponse.data.stripeSecretKey || '',
            }))
          }
        } catch (companyError) {
          console.error('Error fetching company payment keys:', companyError)
        }

        // Cargar conexiones WhatsApp disponibles para alertas
        try {
          const whatsappResponse = await api.get('/whatsapp/all?session=0')
          setWhatsappConnections(whatsappResponse.data || [])
        } catch (whatsappError) {
          console.error('Error fetching whatsapp connections:', whatsappError)
        }

      }

      await loadMetaPolicyCompanyContext()
    } catch (error) {
      console.error('Error fetching settings:', error)
      toast.error('Error al cargar configuración')
    } finally {
      setLoading(false)
    }
  }

  const loadMetaPolicyCompanyContext = async () => {
    if (!user?.companyId) return

    let targetCompanyId = Number(selectedMetaPolicyCompanyId || user.companyId)

    try {
      const response = await api.get('/companies/list')
      const companies = Array.isArray(response.data) ? response.data : []
      const normalizedCompanies = companies
        .map((company: any) => ({
          id: Number(company.id),
          name: company.name || `Empresa ${company.id}`
        }))
        .filter((company: MetaPolicyCompanyOption) => Boolean(company.id))

      setMetaPolicyCompanies(normalizedCompanies)

      if (!normalizedCompanies.some((company: MetaPolicyCompanyOption) => company.id === targetCompanyId)) {
        targetCompanyId = normalizedCompanies[0]?.id || Number(user.companyId)
      }
    } catch (error) {
      console.error('Error fetching companies for Meta policies:', error)
      setMetaPolicyCompanies([{ id: Number(user.companyId), name: user?.company?.name || 'Mi empresa' }])
    }

    setSelectedMetaPolicyCompanyId(targetCompanyId)
    await fetchMetaConversionPolicies(targetCompanyId)
  }

  const fetchMetaConversionPolicies = async (companyId?: number | string) => {
    const targetCompanyId = Number(companyId || selectedMetaPolicyCompanyId || user?.companyId)
    if (!targetCompanyId) return
    try {
      setLoadingMetaPolicies(true)
      const response = await api.get(`/facebook-conversions/policies?companyId=${targetCompanyId}`)
      setMetaConversionPolicies(response.data?.policies || [])
    } catch (error: any) {
      console.error('Error fetching Meta conversion policies:', error)
      const apiMsg = error?.response?.data?.error || error?.response?.data?.message
      toast.error(apiMsg || 'No se pudieron cargar los permisos de conversiones Meta')
    } finally {
      setLoadingMetaPolicies(false)
    }
  }

  const updateMetaConversionPolicy = async (
    policy: MetaConversionPolicy,
    enabled: boolean
  ) => {
    const targetCompanyId = Number(selectedMetaPolicyCompanyId || user?.companyId)
    if (!targetCompanyId) return

    const previousPolicies = metaConversionPolicies
    setSavingMetaPolicy(policy.eventKey)
    setMetaConversionPolicies(prev =>
      prev.map(item =>
        item.eventKey === policy.eventKey
          ? { ...item, enabled, source: 'company_override' }
          : item
      )
    )

    try {
      const response = await api.put('/facebook-conversions/policies', {
        companyId: targetCompanyId,
        eventKey: policy.eventKey,
        enabled,
        conversionName:
          policy.conversionName ||
          META_CONVERSION_POLICY_COPY[policy.eventKey].defaultName
      })

      const saved = response.data?.setting
      if (saved) {
        setMetaConversionPolicies(prev =>
          prev.map(item =>
            item.eventKey === policy.eventKey
              ? {
                ...item,
                enabled: saved.enabled,
                conversionName: saved.conversionName || item.conversionName,
                source: 'company_override',
                settingId: saved.id
              }
              : item
          )
        )
      }

      toast.success(enabled ? 'Conversión activada' : 'Conversión desactivada')
    } catch (error: any) {
      setMetaConversionPolicies(previousPolicies)
      const apiMsg = error?.response?.data?.error || error?.response?.data?.message
      toast.error(apiMsg || 'No se pudo guardar el permiso de conversión')
    } finally {
      setSavingMetaPolicy(null)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      // Separar payment keys de otros settings
      const paymentKeys = {
        paypalClientId: settings.paypalClientId,
        paypalSecretKey: settings.paypalSecretKey,
        stripePublicKey: settings.stripePublicKey,
        stripeSecretKey: settings.stripeSecretKey,
      }

      // Skip empty secret fields to avoid overwriting stored values
      const secretFields = ['googleClientSecret', 'facebookAppSecret', 'instagramAppSecret', 'facebookSystemUserToken', 'tiktokClientSecret', 'tiktokBusinessSecret']
      // Columnas protegidas en el backend (UpdateCompanySettingService.PROTECTED_COLUMNS).
      // Se cuelan en settings por el spread de response.data en fetchSettings.
      const protectedColumns = ['id', 'companyId', 'createdAt', 'updatedAt']
      // Payment keys se persisten en /companies/:id, no en /companySettings.
      const paymentKeyFields = ['paypalClientId', 'paypalSecretKey', 'stripePublicKey', 'stripeSecretKey']
      // Settings key/value viven en la tabla Settings y se guardan por su propio endpoint.
      const keyValueSettingFields = ['requireQueueOnAccept']
      const otherSettings = Object.entries(settings).filter(
        ([key, val]) =>
          !protectedColumns.includes(key) &&
          !paymentKeyFields.includes(key) &&
          !keyValueSettingFields.includes(key) &&
          !(secretFields.includes(key) && val === '') &&
          val !== null &&
          val !== undefined
      )

      // Guardar cada configuración (excluyendo payment keys)
      for (const [column, data] of otherSettings) {
        await api.put('/companySettings/', {
          column,
          data: String(data)
        })
      }

      // Solo para superadmin: guardar payment keys en company
      if (isSuperAdmin) {
        await api.put(`/companies/${user?.companyId}`, paymentKeys)
      }

      // Aplicar colores del tema inmediatamente
      setColors({
        primaryLight: settings.themePrimaryLight,
        primaryDark: settings.themePrimaryDark,
        secondaryLight: settings.themeSecondaryLight,
        secondaryDark: settings.themeSecondaryDark,
      })

      toast.success('Configuración guardada exitosamente')
    } catch (error) {
      console.error('Error saving settings:', error)
      toast.error('Error al guardar configuración')
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  /* ── Avisos de citas de Google Calendar ──────────────────────────────── */

  const cargarConfigGoogleCitas = async () => {
    try {
      const [cfg, grupos] = await Promise.all([
        api.get('/google-appointments/config'),
        api.get('/tags/alert-groups')
      ])
      setGaConfig(cfg.data)
      setGruposDisponibles(Array.isArray(grupos.data) ? grupos.data : [])
    } catch (error) {
      console.error('Error cargando config de citas Google:', error)
      setGaConfig(null)
    }
  }

  /**
   * El grupo por conexión se guarda al instante con su propio endpoint: vive en
   * la tabla Whatsapps, no en CompaniesSettings, así que no entra en el guardado
   * masivo de esta pantalla.
   */
  const guardarGrupoConexion = async (whatsappId: number, groupTicketId: number) => {
    try {
      await api.put(`/google-appointments/connection/${whatsappId}`, {
        alertGroupTicketId: groupTicketId || null
      })
      setGaConfig((prev: any) =>
        prev
          ? {
              ...prev,
              conexiones: prev.conexiones.map((c: any) =>
                c.id === whatsappId
                  ? { ...c, alertGroupTicketId: groupTicketId || null }
                  : c
              )
            }
          : prev
      )
      toast.success('Grupo asignado')
    } catch (error: any) {
      console.error('Error asignando grupo:', error)
      toast.error(
        error?.response?.status === 403
          ? 'Solo un administrador puede cambiar esto'
          : 'No se pudo asignar el grupo'
      )
    }
  }

  const activarEscuchaGoogle = async () => {
    try {
      setGaActivando(true)
      const { data } = await api.post('/google-appointments/activate')
      data?.ok ? toast.success(data.mensaje) : toast.error(data?.mensaje || 'No se pudo activar')
      await cargarConfigGoogleCitas()
    } catch (error) {
      console.error('Error activando escucha:', error)
      toast.error('No se pudo activar la escucha')
    } finally {
      setGaActivando(false)
    }
  }

  const sincronizarGoogleAhora = async () => {
    try {
      setGaSincronizando(true)
      const { data } = await api.post('/google-appointments/sync-now')
      if (data?.ok) {
        toast.success(
          `Revisados ${data.eventosRevisados} · avisos ${data.avisosEnviados} · ` +
            `sin teléfonos ${data.descartadosSinTelefonos}`
        )
      } else {
        toast.error(`No se pudo sincronizar: ${data?.motivo || 'error'}`)
      }
      await cargarConfigGoogleCitas()
    } catch (error) {
      console.error('Error sincronizando:', error)
      toast.error('No se pudo sincronizar')
    } finally {
      setGaSincronizando(false)
    }
  }

  // Settings que viven en la tabla "Settings" (key/value), no en "CompaniesSettings".
  // Se persisten inmediatamente con su propio endpoint para no entrar en el bulk save.
  const updateKeyValueSetting = async (key: string, value: string) => {
    // Optimistic UI
    const previousValue = (settings as any)[key]
    setSettings(prev => ({ ...prev, [key]: value }))
    try {
      await api.put(`/settings/${key}`, { value })
      toast.success('Preferencia guardada')
    } catch (error: any) {
      // Rollback
      setSettings(prev => ({ ...prev, [key]: previousValue }))
      const apiMsg = error?.response?.data?.error || error?.response?.data?.message
      toast.error(apiMsg || 'No se pudo guardar la preferencia')
    }
  }

  if (authLoading || loading) {
    return (
      <Container maxWidth="xl">
        <Typography>Cargando configuración...</Typography>
      </Container>
    )
  }

  if (!user?.companyId) {
    return (
      <Container maxWidth="xl">
        <Typography color="danger">Error: No se encontró información de la compañía. Por favor, inicia sesión nuevamente.</Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography level="h2">Configuración Avanzada</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Configuración completa del sistema
              </Typography>
            </Box>
          </Stack>
          <Button
            startDecorator={<SaveIcon />}
            color="primary"
            onClick={handleSave}
            loading={saving}
          >
            Guardar Cambios
          </Button>
        </Stack>

        <Card>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
            <TabList>
              <Tab value={0}>
                <TimerIcon sx={{ mr: 1 }} />
                Tickets
              </Tab>
              <Tab value={1}>
                <WhatsAppIcon sx={{ mr: 1 }} />
                WhatsApp
              </Tab>
              <Tab value={2}>
                <MessageIcon sx={{ mr: 1 }} />
                Mensajes
              </Tab>
              <Tab value={3}>
                <ShieldIcon sx={{ mr: 1 }} />
                LGPD/Privacidad
              </Tab>
              <Tab value={4}>
                <UserIcon sx={{ mr: 1 }} />
                Usuarios
              </Tab>
              <Tab value={5}>
                <SettingsIcon sx={{ mr: 1 }} />
                General
              </Tab>
              <Tab value={6}>
                <PaletteIcon sx={{ mr: 1 }} />
                Tema
              </Tab>
              <Tab value={7}>
                <CampaignIcon sx={{ mr: 1 }} />
                Facebook Ads
              </Tab>
              {/* Pestaña TikTok ocultada a pedido. El TabPanel value={8} queda
                  inactivo (sin tab que lo active). Reactivar restaurando este Tab.
              <Tab value={8}>
                <MusicNoteIcon sx={{ mr: 1 }} />
                TikTok
              </Tab>
              */}
              {isSuperAdmin && (
                <Tab value={9}>
                  <PaymentIcon sx={{ mr: 1 }} />
                  Pagos
                </Tab>
              )}
            </TabList>

            {/* TAB 1: Configuración de Tickets */}
            <TabPanel value={0}>
              <Stack spacing={3}>
                <Typography level="h4">Configuración de Tickets</Typography>
                <Divider />

                <FormControl>
                  <FormLabel>Horas para Cerrar Tickets Automáticamente</FormLabel>
                  <Input
                    type="number"
                    value={settings.hoursCloseTicketsAuto}
                    onChange={(e) => updateSetting('hoursCloseTicketsAuto', e.target.value)}
                  />
                  <FormHelperText>
                    Tiempo en horas después del cual los tickets resueltos se cerrarán automáticamente
                  </FormHelperText>
                </FormControl>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Tickets Directos a Billeteras
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Los tickets se asignan directamente a las billeteras de usuarios
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.DirectTicketsToWallets}
                    onChange={(e) => updateSetting('DirectTicketsToWallets', e.target.checked)}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Cerrar Ticket al Transferir
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Cierra automáticamente el ticket cuando se transfiere a otro usuario
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.closeTicketOnTransfer}
                    onChange={(e) => updateSetting('closeTicketOnTransfer', e.target.checked)}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Exigir Cola al Aceptar Ticket
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Si está activado, no se permite aceptar un ticket pendiente sin asignarle una cola.
                      Si está desactivado, se puede aceptar sin cola (solo se guarda un warning en logs).
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.requireQueueOnAccept === 'enabled'}
                    onChange={(e) => updateKeyValueSetting('requireQueueOnAccept', e.target.checked ? 'enabled' : 'disabled')}
                  />
                </Box>

                <FormControl>
                  <FormLabel>Tipo de Chatbot</FormLabel>
                  <Select
                    value={settings.chatBotType}
                    onChange={(_, value) => updateSetting('chatBotType', value)}
                  >
                    <Option value="text">Texto</Option>
                    <Option value="button">Botones</Option>
                    <Option value="list">Lista</Option>
                  </Select>
                  <FormHelperText>Tipo de interfaz para el chatbot automatizado</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Tipo de Programación</FormLabel>
                  <Select
                    value={settings.scheduleType}
                    onChange={(_, value) => updateSetting('scheduleType', value)}
                  >
                    <Option value="1">Por Empresa</Option>
                    <Option value="2">Por Conexión</Option>
                  </Select>
                  <FormHelperText>Define cómo se manejan los horarios de atención</FormHelperText>
                </FormControl>
              </Stack>
            </TabPanel>

            {/* TAB 2: WhatsApp */}
            <TabPanel value={1}>
              <Stack spacing={3}>
                <Typography level="h4">Configuración de WhatsApp</Typography>
                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Aceptar Llamadas de WhatsApp
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Si se desactiva, las llamadas entrantes se rechazan y se envía el mensaje configurado en cada conexión. Si la conexión no tiene mensaje, no se envía nada.
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.acceptCallWhatsapp === 'enabled'}
                    onChange={(e) =>
                      updateSetting('acceptCallWhatsapp', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Aceptar Mensajes de Audio (política global)
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Política por defecto para todas las conexiones. Cada conexión puede sobreescribirla en su pestaña Permisos.
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.acceptAudioMessageContact === 'enabled'}
                    onChange={(e) =>
                      updateSetting(
                        'acceptAudioMessageContact',
                        e.target.checked ? 'enabled' : 'disabled'
                      )
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Verificar Mensajes de Grupos
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Verificar si los mensajes provienen de grupos de WhatsApp
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.CheckMsgIsGroup === 'enabled'}
                    onChange={(e) =>
                      updateSetting('CheckMsgIsGroup', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>
              </Stack>
            </TabPanel>

            {/* TAB 3: Mensajes */}
            <TabPanel value={2}>
              <Stack spacing={3}>
                <Typography level="h4">Configuración de Mensajes</Typography>
                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Mensaje de Bienvenida por Cola
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Mensaje automático cuando el cliente elige una cola
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendGreetingMessageOneQueues === 'enabled'}
                    onChange={(e) =>
                      updateSetting(
                        'sendGreetingMessageOneQueues',
                        e.target.checked ? 'enabled' : 'disabled'
                      )
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Mensaje de Firma
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Agregar firma automática a los mensajes enviados
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendSignMessage === 'enabled'}
                    onChange={(e) =>
                      updateSetting('sendSignMessage', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Despedida en Ticket en Espera
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Mensaje cuando el ticket pasa a estado de espera
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendFarewellWaitingTicket === 'enabled'}
                    onChange={(e) =>
                      updateSetting(
                        'sendFarewellWaitingTicket',
                        e.target.checked ? 'enabled' : 'disabled'
                      )
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Saludo al Aceptar Ticket
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Mensaje automático cuando un agente acepta un ticket
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendGreetingAccepted === 'enabled'}
                    onChange={(e) =>
                      updateSetting(
                        'sendGreetingAccepted',
                        e.target.checked ? 'enabled' : 'disabled'
                      )
                    }
                  />
                </Box>

                <FormControl>
                  <FormLabel>Mensaje al Aceptar Ticket</FormLabel>
                  <Textarea
                    minRows={2}
                    value={settings.greetingAcceptedMessage}
                    onChange={(e) => updateSetting('greetingAcceptedMessage', e.target.value)}
                    disabled={settings.sendGreetingAccepted === 'disabled'}
                  />
                </FormControl>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Mensaje al Transferir Ticket
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Notificar al cliente cuando su ticket es transferido
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendMsgTransfTicket === 'enabled'}
                    onChange={(e) =>
                      updateSetting(
                        'sendMsgTransfTicket',
                        e.target.checked ? 'enabled' : 'disabled'
                      )
                    }
                  />
                </Box>

                <FormControl>
                  <FormLabel>Mensaje de Transferencia</FormLabel>
                  <Textarea
                    minRows={2}
                    value={settings.transferMessage}
                    onChange={(e) => updateSetting('transferMessage', e.target.value)}
                    disabled={settings.sendMsgTransfTicket === 'disabled'}
                  />
                </FormControl>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Enviar Posición en Cola
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Informar al cliente su posición en la cola de espera
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.sendQueuePosition === 'enabled'}
                    onChange={(e) =>
                      updateSetting('sendQueuePosition', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <FormControl>
                  <FormLabel>Mensaje de Posición en Cola</FormLabel>
                  <Textarea
                    minRows={2}
                    value={settings.sendQueuePositionMessage}
                    onChange={(e) => updateSetting('sendQueuePositionMessage', e.target.value)}
                    disabled={settings.sendQueuePosition === 'disabled'}
                  />
                  <FormHelperText>
                    Variables disponibles: {'{{position}}'} para la posición en cola
                  </FormHelperText>
                </FormControl>
              </Stack>
            </TabPanel>

            {/* TAB 4: LGPD/Privacidad */}
            <TabPanel value={3}>
              <Stack spacing={3}>
                <Typography level="h4">LGPD y Privacidad de Datos</Typography>
                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Habilitar LGPD
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Activar funciones de protección de datos personales
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.enableLGPD === 'enabled'}
                    onChange={(e) =>
                      updateSetting('enableLGPD', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Solicitar Consentimiento
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Pedir autorización explícita antes de procesar datos
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.lgpdConsent === 'enabled'}
                    onChange={(e) =>
                      updateSetting('lgpdConsent', e.target.checked ? 'enabled' : 'disabled')
                    }
                    disabled={settings.enableLGPD === 'disabled'}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Eliminar Mensajes LGPD
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Permitir eliminación de mensajes por solicitud del usuario
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.lgpdDeleteMessage === 'enabled'}
                    onChange={(e) =>
                      updateSetting('lgpdDeleteMessage', e.target.checked ? 'enabled' : 'disabled')
                    }
                    disabled={settings.enableLGPD === 'disabled'}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Ocultar Números de Teléfono
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Enmascarar parcialmente los números de teléfono
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.lgpdHideNumber === 'enabled'}
                    onChange={(e) =>
                      updateSetting('lgpdHideNumber', e.target.checked ? 'enabled' : 'disabled')
                    }
                    disabled={settings.enableLGPD === 'disabled'}
                  />
                </Box>

                <FormControl>
                  <FormLabel>Mensaje LGPD</FormLabel>
                  <Textarea
                    minRows={3}
                    value={settings.lgpdMessage}
                    onChange={(e) => updateSetting('lgpdMessage', e.target.value)}
                    disabled={settings.enableLGPD === 'disabled'}
                  />
                  <FormHelperText>
                    Mensaje informativo sobre el tratamiento de datos personales
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Link de Política de Privacidad</FormLabel>
                  <Input
                    type="url"
                    value={settings.lgpdLink}
                    onChange={(e) => updateSetting('lgpdLink', e.target.value)}
                    disabled={settings.enableLGPD === 'disabled'}
                  />
                  <FormHelperText>URL completa de su política de privacidad</FormHelperText>
                </FormControl>
              </Stack>
            </TabPanel>

            {/* TAB 5: Usuarios */}
            <TabPanel value={4}>
              <Stack spacing={3}>
                <Typography level="h4">Configuración de Usuarios</Typography>
                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Asignación Aleatoria de Usuarios
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Distribuir tickets aleatoriamente entre usuarios disponibles
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.userRandom === 'enabled'}
                    onChange={(e) =>
                      updateSetting('userRandom', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Encuesta NPS al cerrar ticket (política global)
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Activa la solicitud de calificación al cerrar tickets. El texto que se envía y la activación específica por conexión se configuran dentro de cada conexión (pestañas Mensajes y Permisos).
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.userRating === 'enabled'}
                    onChange={(e) =>
                      updateSetting('userRating', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Mostrar Notificaciones Pendientes
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Notificar a los usuarios sobre tickets pendientes
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.showNotificationPending}
                    onChange={(e) => updateSetting('showNotificationPending', e.target.checked)}
                  />
                </Box>
              </Stack>
            </TabPanel>

            {/* TAB 6: General */}
            <TabPanel value={5}>
              <Stack spacing={3}>
                <Typography level="h4">Configuracion General</Typography>
                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" fontWeight="bold">
                      Etiqueta Requerida
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Obligar a asignar etiquetas a los tickets
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.requiredTag === 'enabled'}
                    onChange={(e) =>
                      updateSetting('requiredTag', e.target.checked ? 'enabled' : 'disabled')
                    }
                  />
                </Box>

                <Divider />

                {/* Google Calendar Integration */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 1 }}>
                    Google Calendar
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Credenciales OAuth para sincronizar citas con Google Calendar.
                    Cada empresa configura sus propias credenciales.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Google Client ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.googleClientId}
                        onChange={(e) => updateSetting('googleClientId', e.target.value)}
                        placeholder="xxxxx.apps.googleusercontent.com"
                      />
                      <FormHelperText>
                        Client ID de tu proyecto en Google Cloud Console
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Google Client Secret</FormLabel>
                      <Input
                        type="password"
                        value={settings.googleClientSecret}
                        onChange={(e) => updateSetting('googleClientSecret', e.target.value)}
                        placeholder="GOCSPX-xxxxx"
                      />
                      <FormHelperText>
                        Client Secret de tu proyecto en Google Cloud Console
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Como obtener las credenciales de Google:
                  </Typography>
                  <Typography level="body-xs" component="div" sx={{ pl: 2 }}>
                    <ol style={{ margin: 0, paddingLeft: '1rem' }}>
                      <li>Ve a <strong>console.cloud.google.com</strong></li>
                      <li>Crea un proyecto nuevo o selecciona uno existente</li>
                      <li>En <strong>APIs & Services - Library</strong>, habilita <strong>Google Calendar API</strong></li>
                      <li>En <strong>APIs & Services - Credentials</strong>, crea un <strong>OAuth client ID</strong> (tipo Web application)</li>
                      <li>Configura el <strong>Authorized redirect URI</strong> con la URL de tu servidor + <code>/api/appointments/calendar/google/callback</code></li>
                      <li>Copia el <strong>Client ID</strong> y <strong>Client Secret</strong> y pegalos aqui</li>
                    </ol>
                  </Typography>
                </Box>

                {/* ── Avisos de citas creadas en Google Calendar ──────────── */}
                <Divider />
                <Box>
                  <Typography level="title-lg" sx={{ mb: 1 }}>
                    Avisos de citas de Google
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Cuando un agente externo crea una cita en Google Calendar, ChatEAM
                    avisa al grupo de WhatsApp que corresponda. No crea tickets ni
                    contactos: solo notifica.
                  </Typography>

                  {!gaConfig ? (
                    <Alert color="neutral" variant="soft" size="sm">
                      Cargando configuración…
                    </Alert>
                  ) : !gaConfig.credencialesConfiguradas ? (
                    <Alert color="warning" variant="soft">
                      Primero completa el <b>Client ID</b> y el <b>Client Secret</b> de
                      Google, arriba, y guarda.
                    </Alert>
                  ) : !gaConfig.calendarioConectado ? (
                    <Alert color="warning" variant="soft">
                      Las credenciales están puestas, pero <b>ninguna cuenta de Google
                      autorizó todavía</b> el acceso al calendario. Sin esa autorización
                      no hay citas que leer.
                    </Alert>
                  ) : (
                    <Stack spacing={2}>
                      {/* Estado de la escucha */}
                      <Alert
                        color={gaConfig.canal?.activo ? 'success' : 'warning'}
                        variant="soft"
                        size="sm"
                      >
                        <Box>
                          <Typography level="title-sm">
                            {gaConfig.canal?.activo
                              ? 'Escucha activa'
                              : 'Escucha no activada'}
                          </Typography>
                          <Typography level="body-xs">
                            {gaConfig.canal?.activo
                              ? `Google avisará de cada cambio. Se renueva sola antes de caducar (${new Date(gaConfig.canal.expiraEn).toLocaleDateString('es-EC')}).`
                              : 'Pulsa "Activar escucha" para que Google empiece a avisar de las citas nuevas.'}
                          </Typography>
                        </Box>
                      </Alert>

                      {/* Grupo por defecto */}
                      <FormControl>
                        <FormLabel>Grupo por defecto</FormLabel>
                        <Select
                          placeholder="Elige un grupo…"
                          value={settings.alertDefaultGroupTicketId || null}
                          onChange={(_, v) =>
                            updateSetting('alertDefaultGroupTicketId', v ?? '')
                          }
                        >
                          {gruposDisponibles.map((g: any) => (
                            <Option key={g.ticketId} value={g.ticketId} disabled={!g.available}>
                              {g.groupName} — {g.connectionName}
                              {!g.available ? ` (${g.reason})` : ''}
                            </Option>
                          ))}
                        </Select>
                        <FormHelperText>
                          Recibe las citas cuyo número de negocio no coincide con ninguna
                          conexión. Sin esto, esas citas se descartan.
                        </FormHelperText>
                      </FormControl>

                      {/* Grupo por conexión */}
                      <Box>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>
                          Grupo de cada conexión
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                          Si el «Business phone» de la cita coincide con el número de una
                          conexión, el aviso va a su grupo. Así se distinguen las sedes.
                        </Typography>

                        <Stack spacing={1}>
                          {gaConfig.conexiones.map((c: any) => (
                            <Box
                              key={c.id}
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box sx={{ minWidth: 200 }}>
                                <Typography level="body-sm">{c.name}</Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                  {c.number || 'sin número — no puede enrutar'}
                                </Typography>
                              </Box>
                              <Select
                                size="sm"
                                sx={{ minWidth: 240, flex: 1 }}
                                placeholder={c.puedeEnrutar ? 'Sin grupo' : 'No aplica'}
                                disabled={!c.puedeEnrutar}
                                value={c.alertGroupTicketId ?? null}
                                onChange={(_, v) => guardarGrupoConexion(c.id, v as number)}
                              >
                                <Option value={0}>— Sin grupo —</Option>
                                {gruposDisponibles.map((g: any) => (
                                  <Option
                                    key={g.ticketId}
                                    value={g.ticketId}
                                    disabled={!g.available}
                                  >
                                    {g.groupName} — {g.connectionName}
                                  </Option>
                                ))}
                              </Select>
                            </Box>
                          ))}
                        </Stack>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button
                          size="sm"
                          variant="solid"
                          loading={gaActivando}
                          onClick={activarEscuchaGoogle}
                        >
                          {gaConfig.canal?.activo ? 'Renovar escucha' : 'Activar escucha'}
                        </Button>
                        <Button
                          size="sm"
                          variant="soft"
                          loading={gaSincronizando}
                          onClick={sincronizarGoogleAhora}
                        >
                          Sincronizar ahora
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Box>

                {/* Alertas WhatsApp - Nuevas Empresas (Solo SuperAdmin) */}
                {isSuperAdmin && (
                  <>
                    <Divider />
                    <Box>
                      <Typography level="title-lg" sx={{ mb: 1 }}>
                        <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Alertas WhatsApp — Nuevas Empresas
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                        Enviar notificación por WhatsApp cada vez que se registre una nueva empresa.
                      </Typography>

                      <Stack spacing={2}>
                        {/* Switch habilitar/deshabilitar */}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography level="body-sm" fontWeight="bold">
                              Activar alertas
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              Enviar mensaje WhatsApp con datos de la empresa creada
                            </Typography>
                          </Box>
                          <Switch
                            checked={settings.newCompanyAlertEnabled === 'enabled'}
                            onChange={(e) =>
                              updateSetting('newCompanyAlertEnabled', e.target.checked ? 'enabled' : 'disabled')
                            }
                          />
                        </Box>

                        {/* Campos solo visibles si la alerta está habilitada */}
                        {settings.newCompanyAlertEnabled === 'enabled' && (
                          <>
                            {/* Select de conexión WhatsApp */}
                            <FormControl>
                              <FormLabel>Conexión WhatsApp para enviar alertas</FormLabel>
                              <Select
                                value={settings.newCompanyAlertWhatsappId ? String(settings.newCompanyAlertWhatsappId) : ''}
                                onChange={(_e, value) => updateSetting('newCompanyAlertWhatsappId', value || '')}
                                placeholder="Selecciona una conexión..."
                              >
                                {whatsappConnections
                                  .filter((w) => w.status === 'CONNECTED')
                                  .map((w) => (
                                    <Option key={w.id} value={String(w.id)}>
                                      {w.name} {w.number ? `(${w.number})` : ''} — {w.channel || 'whatsapp'}
                                      {w.Company ? ` [${w.Company.name}]` : ''}
                                    </Option>
                                  ))}
                              </Select>
                              <FormHelperText>
                                Solo se muestran conexiones con estado CONNECTED
                              </FormHelperText>
                            </FormControl>

                            {/* Lista dinámica de números destino */}
                            <FormControl>
                              <FormLabel>Números de teléfono destino</FormLabel>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                                Agrega los números que recibirán la alerta (con código de país, ej: 521234567890)
                              </Typography>

                              {/* Números actuales */}
                              {settings.newCompanyAlertPhone && settings.newCompanyAlertPhone.split(',').filter(Boolean).length > 0 && (
                                <List size="sm" sx={{ mb: 1 }}>
                                  {settings.newCompanyAlertPhone.split(',').filter(Boolean).map((phone: string, idx: number) => (
                                    <ListItem
                                      key={idx}
                                      endAction={
                                        <IconButton
                                          size="sm"
                                          color="danger"
                                          variant="plain"
                                          onClick={() => {
                                            const phones = settings.newCompanyAlertPhone
                                              .split(',')
                                              .filter(Boolean)
                                              .filter((_: string, i: number) => i !== idx)
                                            updateSetting('newCompanyAlertPhone', phones.join(','))
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      }
                                    >
                                      <ListItemDecorator>
                                        <PhoneIcon fontSize="small" />
                                      </ListItemDecorator>
                                      <ListItemContent>
                                        <Typography level="body-sm">{phone.trim()}</Typography>
                                      </ListItemContent>
                                    </ListItem>
                                  ))}
                                </List>
                              )}

                              {/* Input para agregar nuevo número */}
                              <Stack direction="row" spacing={1}>
                                <Input
                                  type="tel"
                                  value={newAlertPhone}
                                  onChange={(e) => setNewAlertPhone(e.target.value.replace(/[^0-9+]/g, ''))}
                                  placeholder="521234567890"
                                  sx={{ flex: 1 }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newAlertPhone.trim()) {
                                      e.preventDefault()
                                      const current = settings.newCompanyAlertPhone
                                        ? settings.newCompanyAlertPhone.split(',').filter(Boolean)
                                        : []
                                      if (!current.includes(newAlertPhone.trim())) {
                                        updateSetting('newCompanyAlertPhone', [...current, newAlertPhone.trim()].join(','))
                                      }
                                      setNewAlertPhone('')
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  startDecorator={<AddIcon />}
                                  disabled={!newAlertPhone.trim()}
                                  onClick={() => {
                                    const current = settings.newCompanyAlertPhone
                                      ? settings.newCompanyAlertPhone.split(',').filter(Boolean)
                                      : []
                                    if (!current.includes(newAlertPhone.trim())) {
                                      updateSetting('newCompanyAlertPhone', [...current, newAlertPhone.trim()].join(','))
                                    }
                                    setNewAlertPhone('')
                                  }}
                                >
                                  Agregar
                                </Button>
                              </Stack>
                            </FormControl>
                          </>
                        )}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Alertas WhatsApp — Expiración de empresas */}
                    <Box>
                      <Typography level="title-lg" sx={{ mb: 1 }}>
                        <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#f3a43b' }} />
                        Alertas WhatsApp — Expiración de empresas
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                        Enviar dos avisos por WhatsApp por cada empresa: uno 4 días antes del vencimiento y otro cuando ya expiró.
                      </Typography>

                      <Stack spacing={2}>
                        {/* Switch habilitar/deshabilitar */}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography level="body-sm" fontWeight="bold">
                              Activar alertas
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              Envío automático: aviso a 4 días + aviso al expirar
                            </Typography>
                          </Box>
                          <Switch
                            checked={settings.expirationAlertEnabled === 'enabled'}
                            onChange={(e) =>
                              updateSetting('expirationAlertEnabled', e.target.checked ? 'enabled' : 'disabled')
                            }
                          />
                        </Box>

                        {/* Campos solo visibles si la alerta está habilitada */}
                        {settings.expirationAlertEnabled === 'enabled' && (
                          <>
                            {/* Select de conexión WhatsApp */}
                            <FormControl>
                              <FormLabel>Conexión WhatsApp para enviar alertas</FormLabel>
                              <Select
                                value={settings.expirationAlertWhatsappId ? String(settings.expirationAlertWhatsappId) : ''}
                                onChange={(_e, value) => updateSetting('expirationAlertWhatsappId', value || '')}
                                placeholder="Selecciona una conexión..."
                              >
                                {whatsappConnections
                                  .filter((w) => w.status === 'CONNECTED')
                                  .map((w) => (
                                    <Option key={w.id} value={String(w.id)}>
                                      {w.name} {w.number ? `(${w.number})` : ''} — {w.channel || 'whatsapp'}
                                      {w.Company ? ` [${w.Company.name}]` : ''}
                                    </Option>
                                  ))}
                              </Select>
                              <FormHelperText>
                                Solo se muestran conexiones con estado CONNECTED
                              </FormHelperText>
                            </FormControl>

                            {/* Lista dinámica de números destino */}
                            <FormControl>
                              <FormLabel>Números de teléfono destino</FormLabel>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                                Agrega los números que recibirán los avisos (con código de país, ej: 521234567890)
                              </Typography>

                              {/* Números actuales */}
                              {settings.expirationAlertPhone && settings.expirationAlertPhone.split(',').filter(Boolean).length > 0 && (
                                <List size="sm" sx={{ mb: 1 }}>
                                  {settings.expirationAlertPhone.split(',').filter(Boolean).map((phone: string, idx: number) => (
                                    <ListItem
                                      key={idx}
                                      endAction={
                                        <IconButton
                                          size="sm"
                                          color="danger"
                                          variant="plain"
                                          onClick={() => {
                                            const phones = settings.expirationAlertPhone
                                              .split(',')
                                              .filter(Boolean)
                                              .filter((_: string, i: number) => i !== idx)
                                            updateSetting('expirationAlertPhone', phones.join(','))
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      }
                                    >
                                      <ListItemDecorator>
                                        <PhoneIcon fontSize="small" />
                                      </ListItemDecorator>
                                      <ListItemContent>
                                        <Typography level="body-sm">{phone.trim()}</Typography>
                                      </ListItemContent>
                                    </ListItem>
                                  ))}
                                </List>
                              )}

                              {/* Input para agregar nuevo número */}
                              <Stack direction="row" spacing={1}>
                                <Input
                                  type="tel"
                                  value={newExpirationAlertPhone}
                                  onChange={(e) => setNewExpirationAlertPhone(e.target.value.replace(/[^0-9+]/g, ''))}
                                  placeholder="521234567890"
                                  sx={{ flex: 1 }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newExpirationAlertPhone.trim()) {
                                      e.preventDefault()
                                      const current = settings.expirationAlertPhone
                                        ? settings.expirationAlertPhone.split(',').filter(Boolean)
                                        : []
                                      if (!current.includes(newExpirationAlertPhone.trim())) {
                                        updateSetting('expirationAlertPhone', [...current, newExpirationAlertPhone.trim()].join(','))
                                      }
                                      setNewExpirationAlertPhone('')
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  startDecorator={<AddIcon />}
                                  disabled={!newExpirationAlertPhone.trim()}
                                  onClick={() => {
                                    const current = settings.expirationAlertPhone
                                      ? settings.expirationAlertPhone.split(',').filter(Boolean)
                                      : []
                                    if (!current.includes(newExpirationAlertPhone.trim())) {
                                      updateSetting('expirationAlertPhone', [...current, newExpirationAlertPhone.trim()].join(','))
                                    }
                                    setNewExpirationAlertPhone('')
                                  }}
                                >
                                  Agregar
                                </Button>
                              </Stack>
                            </FormControl>
                          </>
                        )}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* WhatsApp Cloud API / Coexistencia Meta */}
                    <Box>
                      <Typography level="title-lg" sx={{ mb: 1 }}>
                        <CloudIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1877f2' }} />
                        WhatsApp Cloud API (Coexistencia Meta)
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                        Habilita la conexion de numeros WhatsApp via Meta Cloud API.
                        Permite coexistencia: el mismo numero funciona en la API Cloud y en la WhatsApp Business App simultaneamente.
                      </Typography>

                      <Stack spacing={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography level="body-sm" fontWeight="bold">
                              Activar Cloud API
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              Permite crear conexiones Meta via Embedded Signup en el panel de Coexistencia
                            </Typography>
                          </Box>
                          <Switch
                            checked={settings.cloudAPIEnabled === true || settings.cloudAPIEnabled === 'true' as any}
                            onChange={(e) =>
                              updateSetting('cloudAPIEnabled', e.target.checked)
                            }
                            sx={{
                              '--Switch-trackWidth': '48px',
                              '--Switch-trackHeight': '24px',
                            }}
                          />
                        </Box>

                        {(settings.cloudAPIEnabled === true || settings.cloudAPIEnabled === 'true' as any) && (
                          <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>
                              Requisitos para Cloud API:
                            </Typography>
                            <Typography level="body-xs" component="div" sx={{ pl: 2 }}>
                              <ol style={{ margin: 0, paddingLeft: '1rem' }}>
                                <li>Configura <strong>Facebook App ID</strong> y <strong>App Secret</strong> en la pestana Facebook Ads</li>
                                <li>Tu App de Meta debe tener el producto <strong>WhatsApp</strong> agregado</li>
                                <li>Ejecuta el <strong>Setup automatico</strong> desde el Dashboard de Coexistencia</li>
                                <li>Conecta tu numero via <strong>Embedded Signup</strong> en Canales → Coexistencia Meta</li>
                              </ol>
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            </TabPanel>

            {/* TAB 7: Personalizacion del Tema */}
            <TabPanel value={6}>
              <Stack spacing={3}>
                <Typography level="h4">Personalizacion del Tema</Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Configura los colores de la plataforma para tu empresa. Los cambios se aplican al guardar.
                </Typography>
                <Divider />

                {/* Colores Primarios */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Colores Primarios</Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <FormLabel>Color Primario - Modo Claro</FormLabel>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: settings.themePrimaryLight,
                            border: '2px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                        <Input
                          type="color"
                          value={settings.themePrimaryLight}
                          onChange={(e) => updateSetting('themePrimaryLight', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <FormHelperText>{settings.themePrimaryLight}</FormHelperText>
                    </FormControl>

                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <FormLabel>Color Primario - Modo Oscuro</FormLabel>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: settings.themePrimaryDark,
                            border: '2px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                        <Input
                          type="color"
                          value={settings.themePrimaryDark}
                          onChange={(e) => updateSetting('themePrimaryDark', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <FormHelperText>{settings.themePrimaryDark}</FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                {/* Colores Secundarios */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Colores Secundarios</Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <FormLabel>Acento - Modo Claro</FormLabel>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: settings.themeSecondaryLight,
                            border: '2px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                        <Input
                          type="color"
                          value={settings.themeSecondaryLight}
                          onChange={(e) => updateSetting('themeSecondaryLight', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <FormHelperText>{settings.themeSecondaryLight}</FormHelperText>
                    </FormControl>

                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <FormLabel>Acento - Modo Oscuro</FormLabel>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            bgcolor: settings.themeSecondaryDark,
                            border: '2px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                        <Input
                          type="color"
                          value={settings.themeSecondaryDark}
                          onChange={(e) => updateSetting('themeSecondaryDark', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <FormHelperText>{settings.themeSecondaryDark}</FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                <Divider />

                {/* Restaurar predeterminados */}
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<RestartAltIcon />}
                  onClick={() => {
                    updateSetting('themePrimaryLight', '#5BC2D2')
                    updateSetting('themePrimaryDark', '#6FD4E4')
                    updateSetting('themeSecondaryLight', '#4caf50')
                    updateSetting('themeSecondaryDark', '#4caf50')
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Restaurar Colores Predeterminados
                </Button>

                <Divider />

                {/* Vista Previa */}
                <Typography level="title-lg">Vista Previa</Typography>
                <Card variant="outlined" sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    {/* Botones */}
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: 1 }}>Botones</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          sx={{
                            bgcolor: settings.themePrimaryLight,
                            color: '#fff',
                            '&:hover': { bgcolor: settings.themePrimaryLight, filter: 'brightness(0.9)', transform: 'none', boxShadow: 'none' },
                          }}
                        >
                          Boton Primario
                        </Button>
                        <Button
                          variant="outlined"
                          sx={{
                            borderColor: settings.themePrimaryLight,
                            color: settings.themePrimaryLight,
                            '&:hover': { bgcolor: settings.themePrimaryLight + '10', transform: 'none', boxShadow: 'none' },
                          }}
                        >
                          Boton Outlined
                        </Button>
                        <Button
                          variant="soft"
                          sx={{
                            bgcolor: settings.themePrimaryLight + '20',
                            color: settings.themePrimaryLight,
                            '&:hover': { bgcolor: settings.themePrimaryLight + '30', transform: 'none', boxShadow: 'none' },
                          }}
                        >
                          Boton Soft
                        </Button>
                      </Stack>
                    </Box>

                    {/* Chips */}
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: 1 }}>Chips</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip sx={{ bgcolor: settings.themePrimaryLight, color: '#fff' }}>Primario</Chip>
                        <Chip sx={{ bgcolor: settings.themeSecondaryLight, color: '#fff' }}>Secundario</Chip>
                        <Chip variant="outlined" sx={{ borderColor: settings.themePrimaryLight, color: settings.themePrimaryLight }}>Outlined</Chip>
                        <Chip variant="soft" sx={{ bgcolor: settings.themePrimaryLight + '20', color: settings.themePrimaryLight }}>Soft</Chip>
                      </Stack>
                    </Box>

                    {/* Avatar + Sidebar selected */}
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: 1 }}>Elementos</Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: settings.themePrimaryLight, color: '#fff', fontWeight: 700 }}>JR</Avatar>
                        <Box sx={{
                          px: 2, py: 1,
                          borderRadius: '6px',
                          bgcolor: settings.themePrimaryLight,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '14px',
                        }}>
                          Sidebar Seleccionado
                        </Box>
                        <Box sx={{
                          px: 2, py: 1,
                          borderRadius: '18px',
                          bgcolor: settings.themePrimaryLight,
                          color: '#fff',
                          fontSize: '14px',
                        }}>
                          Burbuja de mensaje
                        </Box>
                      </Stack>
                    </Box>

                    {/* Area resaltada */}
                    <Box sx={{
                      p: 2,
                      borderRadius: '8px',
                      bgcolor: settings.themePrimaryLight + '12',
                      border: `1px solid ${settings.themePrimaryLight}40`,
                    }}>
                      <Typography level="body-sm" sx={{ color: settings.themePrimaryLight }}>
                        Area resaltada con el color primario seleccionado
                      </Typography>
                    </Box>

                    {/* Dark mode preview */}
                    <Box sx={{
                      p: 2,
                      borderRadius: '8px',
                      bgcolor: '#18191A',
                    }}>
                      <Typography level="body-xs" sx={{ mb: 1, color: '#8A8D91', textTransform: 'uppercase', letterSpacing: 1 }}>Modo Oscuro</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: settings.themePrimaryDark, color: '#fff', fontWeight: 700, width: 36, height: 36 }}>JR</Avatar>
                        <Box sx={{
                          px: 2, py: 1,
                          borderRadius: '18px',
                          bgcolor: settings.themePrimaryDark,
                          color: '#fff',
                          fontSize: '14px',
                        }}>
                          Burbuja modo oscuro
                        </Box>
                        <Chip sx={{ bgcolor: settings.themePrimaryDark, color: '#fff' }}>Chip Dark</Chip>
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              </Stack>
            </TabPanel>

            {/* TAB 8: Facebook Ads Account Configuration */}
            <TabPanel value={7}>
              <Stack spacing={3}>
                <Typography level="h4">Configuracion de Facebook/Instagram</Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Configura las credenciales de tu aplicacion de Facebook para habilitar el login OAuth
                  en conexiones de Facebook e Instagram.
                </Typography>
                <Divider />

                {/* Seccion: Credenciales de la App (OAuth) */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Credenciales de la App de Facebook
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Estas credenciales son necesarias para el login OAuth de Facebook e Instagram.
                    Obtenlas desde developers.facebook.com en tu aplicacion.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Facebook App ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.facebookAppId}
                        onChange={(e) => updateSetting('facebookAppId', e.target.value)}
                        placeholder="123456789012345"
                      />
                      <FormHelperText>
                        ID de tu aplicacion de Facebook (developers.facebook.com)
                      </FormHelperText>
                    </FormControl>

	                    <FormControl>
	                      <FormLabel>Facebook App Secret</FormLabel>
	                      <Input
                        type={showFacebookSecret ? 'text' : 'password'}
                        value={settings.facebookAppSecret}
                        onChange={(e) => updateSetting('facebookAppSecret', e.target.value)}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        endDecorator={
                          <IconButton
                            variant="plain"
                            color="neutral"
                            onClick={() => setShowFacebookSecret((v) => !v)}
                            aria-label={showFacebookSecret ? 'Ocultar clave' : 'Ver clave'}
                          >
                            {showFacebookSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        }
                      />
                      <FormHelperText>
	                        Clave secreta de tu aplicacion de Facebook
	                      </FormHelperText>
	                    </FormControl>

	                    <FormControl>
	                      <FormLabel>Embedded Signup Configuration ID</FormLabel>
	                      <Input
	                        type="text"
	                        value={settings.metaEmbeddedSignupConfigId}
	                        onChange={(e) => updateSetting('metaEmbeddedSignupConfigId', e.target.value)}
	                        placeholder="1203192945295067"
	                      />
	                      <FormHelperText>
	                        ID de configuracion de Embedded Signup de esta empresa. Debe pertenecer a la misma App ID.
	                      </FormHelperText>
	                    </FormControl>
	                  </Stack>
	                </Box>

                <Divider />

                {/* Seccion: Credenciales de Instagram (opcional) */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Credenciales de Instagram (Opcional)
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Si tienes una app separada para Instagram, configura sus credenciales aqui.
                    Si usas la misma app de Facebook, puedes dejar estos campos vacios.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Instagram App ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.instagramAppId}
                        onChange={(e) => updateSetting('instagramAppId', e.target.value)}
                        placeholder="123456789012345"
                      />
                      <FormHelperText>
                        ID de tu aplicacion de Instagram (si es diferente a Facebook)
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Instagram App Secret</FormLabel>
                      <Input
                        type="password"
                        value={settings.instagramAppSecret}
                        onChange={(e) => updateSetting('instagramAppSecret', e.target.value)}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <FormHelperText>
                        Clave secreta de tu aplicacion de Instagram
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                <Divider />

                {/* Seccion: Cuenta Publicitaria */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Cuenta Publicitaria (Opcional)
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Configura tu cuenta publicitaria para ver campanas y metricas.
                    Nota: Tambien puedes configurar esto desde Conexiones - Facebook - Credenciales.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Ad Account ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.facebookAdAccountId}
                        onChange={(e) => updateSetting('facebookAdAccountId', e.target.value)}
                        placeholder="123456789"
                      />
                      <FormHelperText>
                        ID de la cuenta publicitaria (sin el prefijo act_)
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Business Manager ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.facebookBusinessId}
                        onChange={(e) => updateSetting('facebookBusinessId', e.target.value)}
                        placeholder="123456789"
                      />
                      <FormHelperText>
                        ID del Business Manager (opcional)
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Access Token</FormLabel>
                      <Input
                        type="password"
                        value={settings.facebookSystemUserToken}
                        onChange={(e) => updateSetting('facebookSystemUserToken', e.target.value)}
                        placeholder="EAAxxxxxxx..."
                      />
                      <FormHelperText>
                        Token con permisos: ads_read, ads_management, read_insights
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                {visibleMetaConversionPolicyKeys.length > 0 && (
                  <>
                    <Divider />

                    {/* Seccion: Politicas de conversiones Meta */}
                    <Box>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={1.5}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Typography level="title-lg">
                            Permisos de conversiones Meta
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                            Controla que eventos de Conversions API puede enviar esta empresa.
                            Si un permiso esta apagado, el backend bloquea ese envio aunque el flujo lo dispare.
                          </Typography>
                        </Box>
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<RestartAltIcon />}
                          loading={loadingMetaPolicies}
                          disabled={!selectedMetaPolicyCompanyNumber}
                          onClick={() => fetchMetaConversionPolicies()}
                        >
                          Actualizar
                        </Button>
                      </Stack>

                      <Box sx={{ p: 2, mb: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        {isSuperAdmin ? (
                          <FormControl>
                            <FormLabel>Empresa a configurar</FormLabel>
                            <Select
                              value={selectedMetaPolicyCompanyId ? String(selectedMetaPolicyCompanyId) : ''}
                              onChange={(_event, value) => {
                                const nextCompanyId = Number(value)
                                if (!nextCompanyId) return
                                setSelectedMetaPolicyCompanyId(nextCompanyId)
                                fetchMetaConversionPolicies(nextCompanyId)
                              }}
                              placeholder="Selecciona una empresa"
                            >
                              {metaPolicyCompanies.map((company) => (
                                <Option key={company.id} value={String(company.id)}>
                                  {company.name} #{company.id}
                                </Option>
                              ))}
                            </Select>
                            <FormHelperText>
                              Estos permisos solo se muestran para empresas autorizadas: #1 y #8.
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <Box>
                            <Typography level="body-sm" fontWeight="bold">
                              Empresa configurada
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                              Estos permisos aplican solo a tu empresa. Un admin de otra empresa no puede cambiarlos.
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {loadingMetaPolicies ? (
                        <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                          <Typography level="body-sm">Cargando permisos de conversiones...</Typography>
                        </Box>
                      ) : metaConversionPolicies.length === 0 ? (
                        <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                          <Typography level="body-sm">
                            No se pudieron cargar permisos para esta empresa. Usa Actualizar para intentar de nuevo.
                          </Typography>
                        </Box>
                      ) : (
                        <Stack spacing={1.25}>
                          {visibleMetaConversionPolicyKeys.map((eventKey) => {
                            const copy = META_CONVERSION_POLICY_COPY[eventKey]
                            const policy = metaConversionPolicies.find(item => item.eventKey === eventKey) || {
                              eventKey,
                              enabled: copy.defaultEnabled,
                              conversionName: copy.defaultName,
                              source: 'default' as const
                            }
                            const isSaving = savingMetaPolicy === eventKey
                            const isCustom = policy.source === 'company_override'

                            return (
                              <Card
                                key={eventKey}
                                variant="outlined"
                                sx={{
                                  borderRadius: 8,
                                  p: 2,
                                  bgcolor: policy.enabled ? 'background.body' : 'background.level1'
                                }}
                              >
                                <Stack
                                  direction={{ xs: 'column', md: 'row' }}
                                  spacing={2}
                                  justifyContent="space-between"
                                  alignItems={{ xs: 'flex-start', md: 'center' }}
                                >
                                  <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                      <Typography level="title-sm">{copy.title}</Typography>
                                      <Chip
                                        size="sm"
                                        color={policy.enabled ? 'success' : 'neutral'}
                                        variant={policy.enabled ? 'soft' : 'outlined'}
                                      >
                                        {policy.enabled ? 'Activo' : 'Apagado'}
                                      </Chip>
                                      <Chip size="sm" variant="outlined" color={isCustom ? 'primary' : 'neutral'}>
                                        {isCustom ? 'Personalizado' : 'Default'}
                                      </Chip>
                                      <Chip size="sm" variant="soft" color="neutral">
                                        {copy.codeLabel ?? eventKey}
                                      </Chip>
                                    </Stack>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                      {copy.description}
                                    </Typography>
                                    {copy.risk && (
                                      <Typography level="body-xs" sx={{ color: 'warning.700' }}>
                                        {copy.risk}
                                      </Typography>
                                    )}
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                      Nombre enviado en custom_data: {policy.conversionName || copy.title}
                                    </Typography>
                                  </Stack>

                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                      {policy.enabled ? 'Enviar' : 'No enviar'}
                                    </Typography>
                                    <Switch
                                      checked={Boolean(policy.enabled)}
                                      disabled={Boolean(savingMetaPolicy && !isSaving)}
                                      onChange={(event) => updateMetaConversionPolicy(policy, event.target.checked)}
                                    />
                                  </Stack>
                                </Stack>
                              </Card>
                            )
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Como obtener las credenciales:
                  </Typography>
                  <Typography level="body-xs" component="div" sx={{ pl: 2 }}>
                    <ol style={{ margin: 0, paddingLeft: '1rem' }}>
                      <li>Ve a <strong>developers.facebook.com</strong></li>
                      <li>Selecciona tu aplicacion o crea una nueva</li>
                      <li>En Configuracion - Basica, copia el <strong>App ID</strong> y <strong>App Secret</strong></li>
                      <li>Asegurate de configurar los productos: Facebook Login, Instagram Basic Display</li>
                    </ol>
                  </Typography>
                </Box>
              </Stack>
            </TabPanel>

            {/* TAB 9: TikTok Credentials Configuration */}
            <TabPanel value={8}>
              <Stack spacing={3}>
                <Typography level="h4">Configuracion de TikTok</Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Configura las credenciales de TikTok para conectar cuentas y responder comentarios.
                  Cada empresa puede tener sus propias credenciales.
                </Typography>
                <Divider />

                {/* Seccion: Credenciales Login Kit */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Credenciales Login Kit (OAuth)
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Necesarias para conectar cuentas de TikTok via OAuth.
                    Obtenlas desde developers.tiktok.com en tu aplicacion Login Kit.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>TikTok Client Key</FormLabel>
                      <Input
                        type="text"
                        value={settings.tiktokClientKey}
                        onChange={(e) => updateSetting('tiktokClientKey', e.target.value)}
                        placeholder="awXXXXXXXXXXXXXX"
                      />
                      <FormHelperText>
                        App ID de tu aplicacion TikTok Login Kit
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>TikTok Client Secret</FormLabel>
                      <Input
                        type="password"
                        value={settings.tiktokClientSecret}
                        onChange={(e) => updateSetting('tiktokClientSecret', e.target.value)}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <FormHelperText>
                        Secret de tu aplicacion TikTok Login Kit
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                <Divider />

                {/* Seccion: Credenciales Business API */}
                <Box>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Credenciales Business API (para responder comentarios)
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Necesarias para responder comentarios via API. Sin esto, solo se pueden leer comentarios.
                    Obtenlas desde business-api.tiktok.com creando una app de tipo Business.
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>TikTok Business App ID</FormLabel>
                      <Input
                        type="text"
                        value={settings.tiktokBusinessAppId}
                        onChange={(e) => updateSetting('tiktokBusinessAppId', e.target.value)}
                        placeholder="123456789"
                      />
                      <FormHelperText>
                        App ID de tu aplicacion TikTok Business API
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>TikTok Business Secret</FormLabel>
                      <Input
                        type="password"
                        value={settings.tiktokBusinessSecret}
                        onChange={(e) => updateSetting('tiktokBusinessSecret', e.target.value)}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <FormHelperText>
                        Secret de tu aplicacion TikTok Business API
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Box>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Como obtener las credenciales:
                  </Typography>
                  <Typography level="body-xs" component="div" sx={{ pl: 2 }}>
                    <ol style={{ margin: 0, paddingLeft: '1rem' }}>
                      <li>Ve a <strong>developers.tiktok.com</strong> y crea una app Login Kit</li>
                      <li>Copia el <strong>Client Key</strong> y <strong>Client Secret</strong></li>
                      <li>Para responder comentarios, ve a <strong>business-api.tiktok.com</strong></li>
                      <li>Crea una app Business y copia el <strong>App ID</strong> y <strong>Secret</strong></li>
                    </ol>
                  </Typography>
                </Box>
              </Stack>
            </TabPanel>

            {/* TAB 10: Payment Configuration (SuperAdmin Only) */}
            {isSuperAdmin && (
              <TabPanel value={9}>
                <Stack spacing={3}>
                  <Typography level="h4">Configuración de Pagos</Typography>
                  <Divider />

                  <Typography level="body-sm" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    ⚠️ Esta sección es visible solo para SuperAdministradores
                  </Typography>

                  {/* PayPal Configuration */}
                  <Box>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      PayPal
                    </Typography>
                    <Stack spacing={2}>
                      <FormControl>
                        <FormLabel>Client ID de PayPal</FormLabel>
                        <Input
                          type="text"
                          value={settings.paypalClientId}
                          onChange={(e) => updateSetting('paypalClientId', e.target.value)}
                          placeholder="Ingresa el Client ID de PayPal"
                        />
                        <FormHelperText>
                          Client ID público de tu cuenta de PayPal
                        </FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Secret Key de PayPal</FormLabel>
                        <Input
                          type="password"
                          value={settings.paypalSecretKey}
                          onChange={(e) => updateSetting('paypalSecretKey', e.target.value)}
                          placeholder="Ingresa el Secret Key de PayPal"
                        />
                        <FormHelperText>
                          Clave secreta de tu cuenta de PayPal (se mantiene oculta)
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Stripe Configuration */}
                  <Box>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Stripe
                    </Typography>
                    <Stack spacing={2}>
                      <FormControl>
                        <FormLabel>Publishable Key de Stripe</FormLabel>
                        <Input
                          type="text"
                          value={settings.stripePublicKey}
                          onChange={(e) => updateSetting('stripePublicKey', e.target.value)}
                          placeholder="Ingresa el Publishable Key de Stripe"
                        />
                        <FormHelperText>
                          Clave pública de tu cuenta de Stripe
                        </FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Secret Key de Stripe</FormLabel>
                        <Input
                          type="password"
                          value={settings.stripeSecretKey}
                          onChange={(e) => updateSetting('stripeSecretKey', e.target.value)}
                          placeholder="Ingresa el Secret Key de Stripe"
                        />
                        <FormHelperText>
                          Clave secreta de tu cuenta de Stripe (se mantiene oculta)
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                  </Box>

                  <Typography level="body-sm" sx={{ mt: 2, color: 'text.tertiary' }}>
                    Las claves de API se utilizan para procesar pagos a través de PayPal y Stripe.
                    Asegúrate de mantener las claves secretas seguras.
                  </Typography>
                </Stack>
              </TabPanel>
            )}
          </Tabs>
        </Card>
      </Stack>
    </Container>
  )
}
